// Phase 1.5 item #1 polish · Prompt diff editor
//
// Production-grade replacement for the v1 textarea. Uses Monaco's
// DiffEditor (already in deps as @monaco-editor/react) which:
//   - shows original on the left + editable modified on the right
//   - highlights inserted / deleted / changed lines inline
//   - same engine as VSCode (folding / find / undo / etc.)
//   - lazy-loads only when this component mounts (overlay opens),
//     so the Inspector page itself doesn't pay the bundle cost
//
// Theme follows the app's dark/light setting via window.matchMedia
// query against `prefers-color-scheme` — Monaco's `vs-dark` for dark,
// `light` for light. This avoids importing the app's theme system
// just for one editor.
import React, { useRef, useEffect, useState } from "react";
import { DiffEditor } from "@monaco-editor/react";
import { COLORS } from "../../../theme/colors";

const PROMPT_LANGUAGE = "markdown";  // closest to system-prompt syntax

export function PromptDiffEditor({ original, modified, onChange, height = "100%" }) {
  const editorRef = useRef(null);
  const [monacoTheme, setMonacoTheme] = useState(() =>
    typeof window !== "undefined"
      && window.matchMedia
      && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "vs-dark" : "light"
  );

  // React to dark/light theme flip
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setMonacoTheme(e.matches ? "vs-dark" : "light");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // Wire onChange — DiffEditor's modified-side change handler.
  // Save the listener disposable so we can release it BEFORE the
  // editor + models dispose. The upstream Monaco race ("TextModel got
  // disposed before DiffEditorWidget model got reset") fires when
  // dispose runs while pending onDidChangeModelContent callbacks are
  // still in flight — pre-disposing the listener avoids that path.
  const listenerRef = useRef(null);
  const handleMount = (editor) => {
    editorRef.current = editor;
    const modifiedEditor = editor.getModifiedEditor?.();
    if (modifiedEditor && onChange) {
      listenerRef.current = modifiedEditor.onDidChangeModelContent(() => {
        onChange(modifiedEditor.getValue());
      });
    }
  };

  // Unmount-time cleanup. Order matters:
  //   1. Dispose our change listener first (stops new events firing).
  //   2. Swap models to placeholder empty models so any deferred
  //      callbacks reference live (empty) models, not disposed ones.
  //   3. Let @monaco-editor/react's own dispose chain run last.
  useEffect(() => {
    return () => {
      try { listenerRef.current?.dispose?.(); } catch { /* noop */ }
      try {
        const editor = editorRef.current;
        if (editor) {
          editor.setModel?.({ original: null, modified: null });
        }
      } catch { /* upstream race tolerated — we tried */ }
    };
  }, []);

  return (
    <div style={{
      flex: 1, minHeight: 0,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 4, overflow: "hidden",
    }}>
      <DiffEditor
        original={original || ""}
        modified={modified || ""}
        language={PROMPT_LANGUAGE}
        theme={monacoTheme}
        height={height}
        // Keep models alive across this component's unmount — the
        // wrapper's default dispose path triggers the upstream race
        // "TextModel got disposed before DiffEditorWidget model got
        // reset" when async callbacks reference the editor mid-dispose.
        // Models leak for the page lifetime; for a modal that opens
        // once per inspector session this is acceptable, and the page
        // reload between sessions reclaims them.
        keepCurrentOriginalModel
        keepCurrentModifiedModel
        options={{
          // VSCode-default-but-tuned-for-prompts
          renderSideBySide: true,        // explicit; left=original, right=edited
          readOnly: false,                // applies to the modified side
          originalEditable: false,        // left side fixed
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 12,
          wordWrap: "on",
          lineNumbers: "on",
          renderLineHighlight: "all",
          diffWordWrap: "on",
          ignoreTrimWhitespace: false,    // whitespace edits matter for prompts
          enableSplitViewResizing: true,
          renderIndicators: true,         // gutter markers for adds/dels
          automaticLayout: true,          // resize w/ container
          // Hide chrome we don't need in a modal
          folding: false,
          glyphMargin: false,
          padding: { top: 8, bottom: 8 },
        }}
        onMount={handleMount}
        loading={
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            height: "100%", color: COLORS.textMuted, fontSize: 12,
          }}>Loading editor…</div>
        }
      />
    </div>
  );
}
